/**
 * MAGSDK basic implementation of UPnP plugin.
 *
 * @author Yaroslav Surilov <y.surilov@infomir.com>
 */

'use strict';

var gettextWrapper = require('mag-gettext'),
    native         = window.stbUPnP,
    isUPnPInit     = false,
    _;


if ( !isUPnPInit ) {
    if ( native ) {
        native.init();
    }
    isUPnPInit = true;
}


/**
 * Construct UPnP worker instance.
 *
 * @constructor
 */
function UPnP () {
    if ( native ) {
        /**
         * Constant types for file type parsing.
         */
        this.TYPE_FOLDER  = native.TYPE_FOLDER;
        this.TYPE_AUDIO   = native.TYPE_AUDIO;
        this.TYPE_VIDEO   = native.TYPE_VIDEO;
        this.TYPE_IMAGE   = native.TYPE_IMAGE;
        this.TYPE_UNKNOWN = native.TYPE_UNKNOWN;
    }
}


/**
 * Get UPnP service state.
 *
 * @return {string} current UPnP state ("lan", "wlan", "off")
 */
UPnP.prototype.getState = function () {
    return core.environment.upnp_conf;
};


/**
 * Add additional information to UPnP items depend on their types.
 *
 * @param {Object[]} data - source data
 *
 * @return {Object[]} UPnP items reached with additional information
 */
UPnP.prototype.prepareUPnPContent = function ( data ) {
    var result = [],
        item   = data.shift();

    while ( item ) {
        switch ( item.ContentType ) {
            case this.TYPE_FOLDER:
                item.type = 9;
                item.url = item.ID;
                break;
            case this.TYPE_AUDIO:
                item.type = 6;
                break;
            case this.TYPE_VIDEO:
                item.type = 5;
                break;
            case this.TYPE_IMAGE:
                item.type = 7;
                break;
            default:
                item.type = 8;
                break;
        }

        if ( item.type !== this.TYPE_FOLDER && item.Resources && item.Resources.length ) {
            item.path = item.Resources[0].Value;
            item.size = item.Resources[0].Size;
        }

        item.Resources = null;
        item.readOnly = true;
        item.name = item.Title;
        result.push(item);
        item = data.shift();
    }

    return result;
};


/**
 * Enter the root UPnP item or open it
 *
 * @param {Object} [data] - media item inner data
 * @param {number} level - hierarchy change flag: 0 - no change, 1 - go level deeper, -1 - go level up
 * @param {Function} callback - method to invoke with a result of operation
 */
UPnP.prototype.openRoot = function ( data, level, callback ) {
    var servers, content;

    if ( typeof callback !== 'function' ) {
        throw new Error('[UPnP::openRoot()] callback must be a function');
    }

    servers = native.getServerListSync();
    content = [];

    // some data received
    if ( servers && Array.isArray(servers) && servers.length > 0 ) {
        servers.forEach(function (server) {
            content.push({name: server.FriendlyName, url: server.UDN, type: 22});
        });
        // go deeper
        //inServer = false;
        //levelChange = level;
        //listLink.total = content.length;
        setTimeout(function () {
            callback( null, content );
        }, 0);
    } else {
        setTimeout(function () {
            callback( {message: 'Unable to get UPnP group servers'} );
        }, 0);
    }
};


/**
 * Open server, setting current server.
 *
 * @param {string} udn - server udn
 * @param {Function} callback - method to invoke with a result of operation
 */
UPnP.prototype.openServer = function ( udn, callback ) {
    if ( typeof callback !== 'function' ) {
        throw new Error('[UPnP::openServer()] callback must be a function');
    }

    native.onOpenServer = function ( data, error ) {
        if ( error ) {
            callback(error);
        } else {
            callback(null, data);
        }
    };

    native.openServer( udn );
};


/**
 * Start retrieving data from the passed stbUPnP.directory.ID.
 *
 * @param {number} id - folder identifier
 * @param {Function} callback - method to invoke with a result of operation
 */
UPnP.prototype.openContext = function ( id, callback ) {
    if ( typeof callback !== 'function' ) {
        throw new Error('[UPnP::openContext()] callback must be a function');
    }

    native.onOpenContext = function ( data, error ) {
        if ( error ) {
            callback(error);
        } else {
            callback(null, data);
        }
    };

    native.openContext( id );
};


/**
 * Set not case sensitive filter by item name with callback.
 * Filter item name on the occurrence of the passed string filter.
 *
 * @param {string} name - string to filter
 * @param {Function} callback - method to invoke with a result of operation
 */
UPnP.prototype.setFilterName = function ( name, callback )  {
    if ( typeof callback !== 'function' ) {
        throw new Error('[UPnP::setFilterName()] callback must be a function');
    }

    if ( typeof name === 'string' ) {
        native.setFilterName(name);
        native.onOpenPage = function ( data, error ) {
            if ( error ) {
                callback(error);
            } else {
                callback(null, data);
            }
        };
        native.openPage(0);
    } else {
        callback({message: 'Argument is not a string'});
    }
};


/**
 * Set not case sensitive filter by item name.
 * Filter item name on the occurrence of the passed string filter.
 *
 * @param {string} name - string to filter
 */
UPnP.prototype.setFilterNameSync = function ( name ) {
    native.setFilterName(name);
};


/**
 * Set filter by item type.
 *
 * @param {number} type - one of the MEDIA_TYPE_* filter type
 *
 * @return {boolean} operation status
 */
UPnP.prototype.setFilterType = function ( type ) {
    return native.setFilterType(type);
};


/**
 * Items per page. Set this attribute, and next 'openContext' call will return setted value.
 *
 * @param {number} size - per page items count
 */
UPnP.prototype.setPageSize = function ( size ) {
    if ( typeof size !== 'number' ) {
        throw new Error('[UPnP::setPageSize()] incorrect size to set, size must be a number');
    }

    native.pageSize = size;
};


/**
 * Construct UPnP setting option.
 *
 * @param {Object} config - API object
 *
 * @return {Object} structured data for passing into Settings
 */
function constructUPnPSettings ( config ) {
    var api = config.api;

    return {
        options: [
            {
                id: 'upnp',
                name: _('Enable UPnP client'),
                description: '',
                icon: 'theme-icon theme-icon-upnp',
                render: api.renders.popupSelect,
                title: _('Select UPnP client'),
                saveImmediate: true,
                environment: {
                    upnp_conf: '' // eslint-disable-line
                },
                prepareForRender: function ( environment, callback ) {
                    var opt = [
                        {value: 'off', name: _('Disabled'), selected: environment['upnp_conf'] === 'off'},
                        {value: 'lan', name: 'LAN', selected: environment['upnp_conf'] === 'lan'},
                        {value: 'wlan', name: _('Wireless'), selected: environment['upnp_conf'] === 'wlan'}
                    ];

                    callback(opt);
                },
                prepareForSave: function ( value, callback ) {
                    this.environment['upnp_conf'] = value;
                    callback();
                    gSTB.ServiceControl('upnp-service', 'restart');
                },
                getter: api.getters.environment,
                saver: api.savers.environment,
                type: 'option',
                parent: 'network'
            }
        ]
    };
}

module.exports = {
    onAppInit: function ( config, callback ) {
        // dome some asynchronous operations
        callback(null, new UPnP(config.app));
    },
    onSettingsInit: function ( config, callback ) {
        gettextWrapper.load({
            name: core.environment.language,
            path: config.path + 'lang'
        }, function () {
            _ = gettextWrapper.gettext;

            callback(null, constructUPnPSettings(config));
        });
    }
};
